/* screens/auth/InviteAccept.jsx — Принятие приглашения (S12, /invite/accept/:token).
   Доступен гостям и авторизованным; единый адаптивный layout (без LeftVisual, фикс №41).
   7 веток: revoked / already_accepted / expired / needs_signin / needs_register /
   ready(match) / ready(mismatch). Токен сохраняется в sessionStorage (pending_invite_token)
   для возврата после входа/регистрации (SignIn после успеха редиректит на /invite/accept/<token>).
   Данные через authReal (adapters/auth.js): preview — PUBLIC (бэк отдаёт invited_email/
   is_authenticated/requires_register + expired/already_accepted/revoked); email_matches бэк НЕ
   шлёт → деривим тут против useAuth().user.email (регистронезависимо). 404 на preview = previewError.
   accept — требует сессию: 400 invitation_expired/_revoked, 403 email_mismatch, 409 already_*.
   ХЕНДОФФ → frontend/src/pages/auth/InviteAccept.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { ScreenHeader } from '../../components/ui/Metric.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { authReal } from '../../lib/api/index.js';
import { RR } from '../../lib/router.jsx';

  const { useState, useEffect } = React;
  const { useNavigate, useParams, Link } = RR;
  const PENDING_INVITE_KEY = 'pending_invite_token';

  function InviteAccept() {
    const navigate = useNavigate();
    const { token } = useParams();
    const { user, login } = useAuth();
    const toast = useToast ? useToast() : null;

    const [preview, setPreview] = useState(null);
    const [phase, setPhase] = useState('loading'); // loading | previewError | ready | accepting | acceptError
    const [acceptErr, setAcceptErr] = useState(null);

    useEffect(() => {
      let alive = true;
      authReal.previewInvitation(token)
        .then((p) => { if (alive) { setPreview(p); setPhase('ready'); } })
        .catch(() => { if (alive) setPhase('previewError'); });
      return () => { alive = false; };
    }, [token]);

    const saveAndGo = (path) => { sessionStorage.setItem(PENDING_INVITE_KEY, token); navigate(path); };
    // безопасный «домой»: гость → вход (/chat под RequireAuth увёл бы на /signin без объяснения), залогинен → ассистент
    const homePath = user ? '/chat' : '/signin';
    // Приглашение на курс «Академии» (AD-6): у него workspace=NULL, поэтому вся
    // «пространственная» лексика экрана для него — ложь. Заголовок, подписи и
    // терминальные тексты ниже ветвятся по этому объекту.
    const courseInvite = (preview && preview.academy_course) || null;

    const accept = async () => {
      setPhase('accepting'); setAcceptErr(null);
      try {
        const res = await authReal.acceptInvitation(token);
        sessionStorage.removeItem(PENDING_INVITE_KEY);
        // Бэк переключил active ws на сервере (session) — синкаем AuthContext (workspaces+active).
        if (login) await login();
        // AD-6: у приглашения две независимые цели — пространство и курс «Академии».
        // Курсовое приглашение НЕ трогает активное пространство (у него workspace=NULL),
        // и уводить человека в чат после «Записаться на курс» значит потерять то
        // единственное, ради чего он перешёл по ссылке из письма.
        const acceptedCourse = (res && res.academy_course) || courseInvite;
        if (acceptedCourse && acceptedCourse.id) {
          if (toast) toast.show({ tone: 'ok', title: 'Вы записаны на курс' });
          navigate(`/learning/c/${acceptedCourse.id}`);
          return;
        }
        if (toast) toast.show({ tone: 'ok', title: 'Вы присоединились к пространству' });
        navigate('/chat');
      } catch (err) {
        // err.code: email_mismatch (403) / invitation_expired / invitation_revoked / already_accepted /
        // already_member — показываем серверный detail (err.message).
        setAcceptErr(err.message || 'Не удалось принять приглашение.');
        setPhase('acceptError');
      }
    };

    // ── loading ──
    if (phase === 'loading') {
      return (
        <div className="auth-form auth-status stack stack-12" aria-busy="true">
          <Spinner size="lg" />
          <ScreenHeader align="center" title="Загружаем приглашение…" />
        </div>
      );
    }

    // ── ошибка превью ──
    if (phase === 'previewError') {
      return (
        <div className="auth-form auth-status stack stack-16">
          <div className="auth-status__glyph auth-status__glyph--bad" aria-hidden="true"><Icon name="alert-circle" size={30} /></div>
          <ScreenHeader align="center" title="Приглашение недоступно" subtitle="Не удалось загрузить данные приглашения. Возможно, ссылка неверна." />
          <Button variant="secondary" size="lg" className="full" onClick={() => navigate(homePath)}>{user ? 'На главную' : 'Перейти ко входу'}</Button>
        </div>
      );
    }

    const p = preview || {};
    const wsName = p.workspace_name || 'пространство';
    const q = (n) => /[«»]/.test(n) ? n : '«' + n + '»';  // не дублируем кавычки в именах

    // ── терминальные ветки (revoked / accepted / expired) ──
    const terminal = (icon, tone, title, sub) => (
      <div className="auth-form auth-status stack stack-16">
        <div className={'auth-status__glyph auth-status__glyph--' + tone} aria-hidden="true"><Icon name={icon} size={30} /></div>
        <ScreenHeader align="center" title={title} subtitle={sub} />
        <Button variant="secondary" size="lg" className="full" onClick={() => navigate(homePath)}>{user ? 'На главную' : 'Перейти ко входу'}</Button>
      </div>
    );
    // Цель приглашения словами: курс или пространство. Без этого приглашённый на
    // курс читал «Доступ к «пространство» больше не действителен» — про сущность,
    // которой в его приглашении нет вовсе.
    const targetQ = courseInvite ? q(courseInvite.title || 'курс') : q(wsName);
    if (p.revoked) {
      return terminal('x-circle', 'bad', 'Приглашение отозвано',
        courseInvite ? `Приглашение на курс ${targetQ} отозвано. Попросите методиста пригласить вас снова.`
          : `Доступ к ${targetQ} больше не действителен. Попросите администратора пригласить вас снова.`);
    }
    if (p.already_accepted) {
      return terminal('check-circle', 'ok', 'Уже принято',
        courseInvite ? `Вы уже записаны на курс ${targetQ}. Откройте его в разделе «Обучение».`
          : `Вы уже состоите в ${targetQ}. Откройте рабочее пространство.`);
    }
    if (p.expired) {
      return terminal('clock', 'warn', 'Срок истёк',
        courseInvite ? `Срок приглашения на курс ${targetQ} истёк. Попросите методиста выслать новое.`
          : `Срок действия приглашения в ${targetQ} истёк. Попросите выслать новое.`);
    }

    // ── активные ветки ──
    // email_matches бэк не присылает — деривим из preview.invited_email vs текущий user.email
    // (регистронезависимо). Бэк всё равно страхует accept'ом 403 email_mismatch.
    const norm = (s) => String(s || '').trim().toLowerCase();
    const emailMatches = !p.invited_email || (!!user && norm(user.email) === norm(p.invited_email));
    const mismatch = p.is_authenticated && !emailMatches;
    const needsRegister = p.requires_register;
    const needsSignin = !p.is_authenticated && !needsRegister;
    const readyMatch = p.is_authenticated && emailMatches;

    return (
      <div className="auth-form auth-invite stack stack-16">
        <div className="auth-invite__crest" aria-hidden="true">
          <Icon name={courseInvite ? 'book' : 'building'} size={28} />
        </div>
        <ScreenHeader align="center"
          eyebrow={courseInvite ? 'Приглашение на курс' : 'Приглашение в команду'}
          title={courseInvite ? <>Начать курс {targetQ}</> : <>Присоединиться к {q(wsName)}</>}
          subtitle={courseInvite
            ? <>Курс откроется сразу после принятия приглашения</>
            : (p.role_name ? <>Вас приглашают как <strong>{p.role_name}</strong></> : null)} />

        <div className="auth-invite__meta">
          {courseInvite
            ? <div className="auth-invite__meta-row"><span className="text-dim">Курс</span><span>{courseInvite.title}</span></div>
            : <div className="auth-invite__meta-row"><span className="text-dim">Пространство</span><span>{wsName}</span></div>}
          {!courseInvite && p.role_name && <div className="auth-invite__meta-row"><span className="text-dim">Роль</span><span>{p.role_name}</span></div>}
          {p.invited_email && <div className="auth-invite__meta-row"><span className="text-dim">Приглашён email</span><span className="auth-invite__email">{p.invited_email}</span></div>}
        </div>

        {mismatch && (
          <div className="callout callout--warn" role="status">
            <Icon name="alert-triangle" size={16} />
            <span>Приглашение для <strong>{p.invited_email}</strong>, а вы вошли под другим аккаунтом. Войдите под нужным адресом.</span>
          </div>
        )}

        {phase === 'acceptError' && acceptErr && (
          <div className="callout callout--bad" role="alert"><Icon name="alert-circle" size={16} /><span>{acceptErr}</span></div>
        )}

        <div className="stack stack-8">
          {readyMatch && !mismatch && (
            <Button variant="primary" size="lg" className="full" loading={phase === 'accepting'} onClick={accept}>
              {phase === 'acceptError' ? 'Повторить'
                : (courseInvite ? 'Начать курс' : 'Принять приглашение')}
            </Button>
          )}
          {mismatch && (
            <Button variant="primary" size="lg" className="full" onClick={() => saveAndGo('/signin')}>Сменить аккаунт</Button>
          )}
          {needsSignin && (
            <Button variant="primary" size="lg" className="full" onClick={() => saveAndGo('/signin')}>Войти, чтобы принять</Button>
          )}
          {needsRegister && (
            <Button variant="primary" size="lg" className="full" onClick={() => saveAndGo('/signup')}>Зарегистрироваться</Button>
          )}
          <Link to={homePath} className="auth-form__alt t-body text-dim auth-invite__skip">{user ? 'На главную' : 'Перейти ко входу'}</Link>
        </div>
      </div>
    );
  }

export { InviteAccept };
